import { useState } from 'react';
import { Icon } from '../../ui';
import { useFieldContext } from '../FieldContext';
import { CELL_AFFORDANCE, stopCellClick } from './cellAffordance';
import { EmptyValue } from './EmptyValue';

/**
 * PHONE is composite (field-types.md decision 3): the canonical E.164 number is
 * what identity resolution matches on, and the string the user typed is kept
 * beside it. The canonical form is what we show; the original is one tap away,
 * because when the two disagree it is usually the canonicalisation that needs
 * checking.
 *
 * Unparseable input has no canonical form. It is then shown raw and flagged -
 * never dropped and never silently "fixed".
 */
export function PhoneDisplay() {
  const { value, linkAsIcon, listRow } = useFieldContext();
  const [showsOriginal, setShowsOriginal] = useState(false);

  if (value === null || typeof value !== 'object' || !('original' in value)) {
    return <EmptyValue />;
  }

  if (value.canonical === null) {
    return (
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <Icon name="warning" size="0.85rem" className="text-warning" />
        <span className="truncate" title="Not a parseable phone number - stored as entered">
          {value.original}
        </span>
      </span>
    );
  }

  const differs = value.canonical !== value.original;
  const shown = showsOriginal ? value.original : value.canonical;

  /**
   * THE TOGGLE SAYS WHAT IT TOGGLES (audit P3-13, its accessible half).
   *
   * Its whole name was "as entered", 109 times on one record page and 21 to 25 times per
   * board column. Read out of context that is not a control, it is a fragment - and the
   * one thing a reader needs to know before pressing it is WHICH number it will swap.
   * Two labels rather than one, because a control whose name does not change with its
   * state is a control a screen reader announces the same way in both.
   *
   * WHAT WAS NOT DONE, and it is a refusal rather than an omission: the tab stops are not
   * removed. The audit reads this as "a tooltip trigger repeated 25 times per column"; it
   * is not a tooltip, it is a real state toggle over two stored strings (`field-types.md`
   * decision 3 keeps both), and DESIGN.md's "hover is never the only affordance" forbids
   * demoting it to something a keyboard cannot reach. The volume is a consequence of
   * rendering the field on a summary CARD at all, which is a decision about what a board
   * card shows.
   */
  const swap = differs && (
    <button
      type="button"
      aria-label={
        showsOriginal
          ? `Show the canonical number instead of ${value.original}`
          : `Show the number as entered instead of ${value.canonical}`
      }
      // 60x18 drawn, 60x40 pressed (`ui-foundation.md` R5, wave 2): a state toggle that
      // a finger cannot hit is a state a touch user does not have.
      className="hit-expand shrink-0 text-2xs text-ink-subtle underline decoration-dotted"
      onClick={(event) => {
        event.stopPropagation();
        setShowsOriginal((current) => !current);
      }}
    >
      {showsOriginal ? 'canonical' : 'as entered'}
    </button>
  );

  // In an EDITABLE cell the number is plain text and the dialer is one icon away (P1-8).
  if (linkAsIcon === true) {
    return (
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate tabular-nums">{shown}</span>
        {swap}
        <a
          href={`tel:${value.canonical}`}
          aria-label={`Call ${value.canonical}`}
          className={CELL_AFFORDANCE}
          onClick={stopCellClick}
        >
          <Icon name="phone" size="0.85rem" />
        </a>
      </span>
    );
  }

  // IN A LIST ROW THE ROW IS THE LINK (P4 round 2, finding F5): see `FieldContext.listRow`.
  // The swap toggle goes with the anchor - a row's secondary line is one line of values, not
  // a place to change which of two stored strings is on screen.
  if (listRow === true) {
    return <span className="truncate tabular-nums">{shown}</span>;
  }

  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {/* No underline under the pointer: the rule and its source are in `UrlDisplay`. */}
      <a
        href={`tel:${value.canonical}`}
        className="truncate tabular-nums text-accent"
        onClick={stopCellClick}
      >
        {shown}
      </a>
      {swap}
    </span>
  );
}
